import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import * as yup from 'yup';
import { Chart } from 'react-google-charts';
import api from 'api/api';
import Form from 'ui/Form/Form';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import Button from 'ui/Button/Button';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { requiredOnly, validationCallback } from 'utils/validation';
import Loader from 'ui/Loader/Loader';
import { changeFieldValue } from 'utils/formMutators';
import { IApiResponse } from 'utils/models';

import { IFormValues, IFXCurrency, CurrentCurrency, IFxRatesResponse, IResponseData } from './FxRates.model';
import fxRatesFields from './ViewFxRateFields';

import styles from './FxRates.module.css';

const schema = yup.object().shape({
  from: requiredOnly('From'),
  to: requiredOnly('To'),
});

const currency: IFXCurrency[] = [
  {
    value: 'ghsRates',
    title: 'GHS – Ghanaian Cedi',
  },
  {
    value: 'usdRates',
    title: 'USD – US Dollar',
  },
  {
    value: 'eurRates',
    title: 'EUR – Euro',
  },
  {
    value: 'gbpRates',
    title: 'GBP – Great Britain Pound',
  },
];

const ViewFxRates = () => {
  const [currentCurency, setCurrentCurrency] = useState<CurrentCurrency>('usdRates');
  const [data, setData] = useState<(string | number)[][]>([['Date', 'GHS']]);
  const [responseData, setResponseData] = useState<IResponseData | null>(null);
  const updateResponseData = useCallback(
    (update: IResponseData) => setResponseData((current) => ({ ...current, ...update })),
    [],
  );
  const [loadingData, setLoadingData] = useState(false);

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const intialValues: IFormValues = useMemo(() => {
    const emptyValues: IFormValues = {
      from: 'ghsRates',
      to: 'usdRates',
    };

    return emptyValues;
  }, []);

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  useEffect(() => {
    setLoadingData(true);

    api
      .get('/reference/fx-rates', {}, true, false)
      .then((response: IFxRatesResponse) => {
        updateResponseData({ eurRates: response.eurRates, gbpRates: response.gbpRates, usdRates: response.usdRates });
      })
      .catch((error: IApiResponse) => {
        console.error('error', error);
      })
      .finally(() => {
        setLoadingData(false);
      });
  }, []);

  useEffect(() => {
    setData([['Date', 'GHS']]);
    if (responseData) {
      const postData: string | number[][] = [];
      responseData[currentCurency].forEach((item) => {
        postData.push([item.fxDate, item.sell]);
      });

      setData((prev) => [...prev, ...postData]);
    }
  }, [currentCurency, responseData]);

  const onSubmit = (values: IFormValues) => {
    setCurrentCurrency(values.to);
  };

  const options = {
    isStacked: true,
    legend: { position: 'none' },
    colors: ['#2D9CDB'],
    fontSize: 10,
    chartArea: {
      width: downMdScreen ? '80%' : '90%',
      height: '80%',
      color: {
        fill: '#F4F4F4',
        opacity: 100,
      },
    },
  };

  let dataLength = 0;

  if (responseData) {
    dataLength = responseData[currentCurency].length - 1;
  }

  return (
    <>
      {loadingData && <Loader />}
      {!loadingData && (
        <DashboardCard>
          <Form
            onSubmit={onSubmit}
            validate={validate}
            mutators={{ changeFieldValue }}
            initialValues={intialValues}
            render={({ handleSubmit, hasValidationErrors }) => (
              <form onSubmit={handleSubmit}>
                <FieldsLeveler label="Currency">{fxRatesFields(currency)}</FieldsLeveler>
                <SubmitLeveler>
                  <Button type="submit" fullWidth disabled={hasValidationErrors}>
                    View Rates
                  </Button>
                </SubmitLeveler>
              </form>
            )}
          />
        </DashboardCard>
      )}
      {!loadingData && responseData && (
        <DashboardCard>
          <div className={styles.chartTitle}>FX Rate for {responseData[currentCurency][dataLength]?.fxDate}</div>
          <div className={styles.chartCardContainer}>
            <div className={styles.chartCard}>
              <div className={styles.chartCardTitle}>You Buy:</div>
              <div className={styles.chartCardValue}>GHS {responseData[currentCurency][dataLength]?.buy}</div>
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartCardTitle}>You Sell:</div>
              <div className={styles.chartCardValue}>GHS {responseData[currentCurency][dataLength]?.sell}</div>
            </div>
          </div>

          <Chart chartType="AreaChart" width="100%" height="350px" data={data} options={options} />
        </DashboardCard>
      )}
    </>
  );
};

export default ViewFxRates;
